import {
  createBrowserRouter,
  ScrollRestoration,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { useEffect } from "react";
import useUserStore from "../stores/user";
import ProtectedRoute from "../components/ProtectedRoute";

import { DefaultLayout } from "../components/layouts/DefaultLayout";
import { HomePage } from "../components/pages/HomePage";
import { ConfirmPage } from "../components/pages/ConfirmPage";
import NotFoundPage from "../components/pages/NotFoundPage";
import { HomeLayout } from "../components/layouts/HomeLayout";
import TermsOfServicePage from "../components/pages/TermsPage";
import PrivacyPolicyPage from "../components/pages/PolicyPace";
import FerpaStatementPage from "../components/pages/FerpaStatementPage";
import AcceptableUsePolicyPage from "../components/pages/AcceptableUsePolicyPage";
import SopipaAcknowledgmentPage from "../components/pages/legal/SopipaAcknowledgmentPage";
import IncidentResponsePage from "../components/pages/legal/IncidentResponsePage";
import DataProcessingAgreementPage from "../components/pages/legal/DataProcessingAgreementPage";
import CoppaPage from "../components/pages/legal/CoppaPage";
import FAQPage from "../components/pages/FAQPage";
import AccessibilityStatementPage from "../components/pages/Accessibility";
import SupportPage from "../components/pages/support/SupportPage";
import ContactSupportPage from "../components/pages/ContactSupportPage";
import FeedbackPage from "../components/pages/support/FeedbackPage";
import LiveChatPage from "../components/pages/support/ChatPage";
import HelpCenterPage from "../components/pages/support/help/HelpCenterPage";
import CreatingYourAccount from "../components/pages/support/help/started/CreatingYourAccount";
import LoggingInAsStudentOrTeacher from "../components/pages/support/help/started/LoggingInAsStudentOrTeacher";
import SettingUpYourFirstClass from "../components/pages/support/help/started/SettingUpYourFirstClass";
import InviteYourStudents from "../components/pages/support/help/started/InviteYourStudents";
import NavigatingTheDashboard from "../components/pages/support/help/started/NavigatingTheDashboard";
import ViewingStudentProgress from "../components/pages/support/help/using/ViewingStudentProgress";
import AssigningActivities from "../components/pages/support/help/using/AssigningActivities";
import ResettingYourPassword from "../components/pages/support/help/settings/ResettingYourPassword";
import ManagingNotifications from "../components/pages/support/help/settings/ManagingNotifications";
import ConnectingYourLMS from "../components/pages/support/help/settings/ConnectingYourLMS";
import UnderstandingPermissionsAndRoles from "../components/pages/support/help/settings/UnderstandingPermissionsAndRoles";
import LtiRegistrationGuide from "../components/pages/support/help/lti-registration-guide";
import StudentDashboard from "../components/pages/student/Dashboard";
import TeacherDashboard from "../components/pages/teacher/Dashboard";
import { DashboardLayout } from "../components/layouts/DashboardLayout";
import AdminDashboard from "../components/pages/admin/dashboard";
import Contact from "../components/pages/Contact";
import { TeacherGuidesLayout } from "../components/layouts/TeacherGuides";
import { brandingConfig } from "../config/branding";
import EdiaLanding from "../components/edia/Landing";
// Run E2 J1: student-teacher chat removed (K-5). ChatPage is no longer routed.
import CleverCallbackPage from "../components/pages/CleverCallbackPage";
import AuthCallback from "../components/pages/AuthCallback";
import SuperAdminDashboard from "../components/pages/superadmin/dashboard";
import AdminRoute from "../components/AdminRoute";
import RoleRoute from "../components/RoleRoute";
import NotificationsPage from "../components/pages/NotificationsPage";
import SubmissionReviewPage from "../components/pages/teacher/SubmissionReviewPage";
import LtiResourcesPage from "../components/pages/LtiResourcesPage";
import LtiChapterPickerPage from "../components/pages/LtiChapterPickerPage";
import SceneReaderPage from "../components/pages/pe-serving/SceneReaderPage";
import StudentLessonPage from "../components/pages/pe-serving/StudentLessonPage";
import TeacherLessonPreview from "../components/pages/pe-serving/TeacherLessonPreview";
import PresentationRoute from "../components/pages/pe-serving/PresentationRoute";
import FormativeAssessmentPage from "../components/pages/pe-serving/FormativeAssessmentPage";
import QuizAssessmentPage from "../components/pages/pe-serving/QuizAssessmentPage";
import HitlQueuePage from "../components/pages/pe-serving/HitlQueuePage";
import LtiAuthWrapper from "../components/LtiAuthWrapper";
import { LtiLayout } from "../components/layouts/LtiLayout";
import { useUrl } from "../composables/useUrl";
import CurriculumPage from "../components/pages/CurriculumPage";
// Run E2 J2: PFL page imports (ChapterPage, ResourcesPage, CommunityPage, GuidedTrainingPage,
// quiz/midterm/finals/project pages, PFL guides, PFL help articles) removed from the router.
// The files are kept; their routes redirect via PflRemovedRedirect.

/**
 * Minimal chrome-free layout for the projection route. Loads the user profile
 * the same way the global Header does (store.fetchUserProfile) — because
 * RoleRoute gates on `profile` and this route renders no Header — then renders
 * the route on a black canvas so the pre-resolve window is black, not white.
 */
function PresentationLayout() {
  const user = useUserStore((s) => s.user);
  const profile = useUserStore((s) => s.profile);
  const fetchUserProfile = useUserStore((s) => s.fetchUserProfile);

  useEffect(() => {
    if (user && !profile) fetchUserProfile(user);
  }, [user, profile, fetchUserProfile]);

  return (
    <div className="fixed inset-0 bg-black">
      <Outlet />
      <ScrollRestoration />
    </div>
  );
}

// Run E2 J1: student-teacher chat removed (K-5). Any old /chat link
// (bookmarks, old notifications) lands on the dashboard for the user's role,
// or on the home page when signed out.
function ChatRemovedRedirect() {
  const user = useUserStore((s) => s.user);
  const profile = useUserStore((s) => s.profile);
  const authResolved = useUserStore((s) => s.authResolved);
  const { getDashboardUrl } = useUrl();

  if (!authResolved) return null;
  if (!user) return <Navigate to="/" replace />;
  if (!profile) return null;
  return <Navigate to={getDashboardUrl()} replace />;
}

// Run E2 J2: PFL Academy (high-school) pages are hidden for Project Explore.
// The files stay in the repo; the routes now land on the matching PE page.
//   to="dashboard":  the signed-in user's dashboard (teacher or student),
//                    or /curriculum when signed out.
//   to="/some/path": that path (search string kept when keepSearch is set).
function PflRemovedRedirect({
  to,
  keepSearch = false,
}: {
  to: "dashboard" | string;
  keepSearch?: boolean;
}) {
  const user = useUserStore((s) => s.user);
  const profile = useUserStore((s) => s.profile);
  const authResolved = useUserStore((s) => s.authResolved);
  const { getDashboardUrl } = useUrl();
  const location = useLocation();

  if (to !== "dashboard") {
    return (
      <Navigate to={keepSearch ? `${to}${location.search}` : to} replace />
    );
  }
  if (!authResolved) return null;
  if (!user) return <Navigate to="/curriculum" replace />;
  if (!profile) return null;
  return <Navigate to={getDashboardUrl()} replace />;
}

export const router = createBrowserRouter([
  {
    // Whole-class presentation / projection (Phase 2.E). Chrome-free full-bleed.
    //
    // RoleRoute blocks on `profile`, which is loaded by the global <Header/>
    // inside DashboardLayout — a header this projection route deliberately omits.
    // Without it `profile` never loads and RoleRoute renders null forever (white
    // screen). This minimal layout reproduces Header's profile load (the same
    // store.fetchUserProfile call) WITHOUT any header chrome, on a black canvas
    // so the pre-resolve state is black — never white. No app shell, no header.
    element: <PresentationLayout />,
    children: [
      {
        path: "/present/:chapterId",
        element: (
          <RoleRoute
            allowedRoles={["teacher", "admin", "district_admin", "super_admin"]}
          >
            <PresentationRoute />
          </RoleRoute>
        ),
      },
    ],
  },
  // Home route with HomeLayout
  {
    element: (
      <>
        <HomeLayout />
        <ScrollRestoration />
      </>
    ),
    children: [
      {
        path: "/",
        element: <HomePage />,
      },
      {
        path: "/login-portal",
        lazy: () => import("../components/pages/LoginPortal"),
      },
      {
        path: "/kid-login",
        lazy: () => import("../components/pages/KidLoginPage"),
      },
      {
        path: "/confirm",
        element: <ConfirmPage />,
      },
      {
        path: "/*",
        element: <NotFoundPage />,
      },
      {
        path: "/reset-password",
        lazy: () => import("../components/pages/ResetPasswordPage"),
      },
      {
        path: "/forgot-password",
        lazy: () => import("../components/pages/ForgotPasswordPage"),
      },
      {
        path: "/standards",
        element: <Navigate to="/curriculum" replace />,
      },
      {
        path: "/curriculum",
        element: <CurriculumPage />,
      },
      {
        path: "/resources",
        element: <PflRemovedRedirect to="/curriculum" />,
      },
      {
        path: "/free-resources",
        element: <PflRemovedRedirect to="/curriculum" />,
      },
      {
        path: "/community",
        element: <PflRemovedRedirect to="/curriculum" />,
      },
      {
        path: "/terms",
        element: <TermsOfServicePage />,
      },
      {
        path: "/privacy",
        element: <PrivacyPolicyPage />,
      },
      {
        path: "/ferpa",
        element: <FerpaStatementPage />,
      },
      {
        path: "/acceptable-use",
        element: <AcceptableUsePolicyPage />,
      },
      {
        path: "/legal/sopipa",
        element: <SopipaAcknowledgmentPage />,
      },
      {
        path: "/legal/incident-response",
        element: <IncidentResponsePage />,
      },
      {
        path: "/legal/dpa",
        element: <DataProcessingAgreementPage />,
      },
      {
        path: "/legal/coppa",
        element: <CoppaPage />,
      },
      {
        path: "/legal/ferpa",
        element: <FerpaStatementPage />,
      },
      {
        path: "/legal/aup",
        element: <AcceptableUsePolicyPage />,
      },
      {
        path: "/faq",
        element: <FAQPage />,
      },
      {
        path: "/accessibility",
        element: <AccessibilityStatementPage />,
      },
      {
        path: "/support",
        element: <SupportPage />,
      },
      {
        path: "/support/feedback",
        element: <FeedbackPage />,
      },
      {
        path: "/support/chat",
        element: <LiveChatPage />,
      },
      {
        path: "/support/guided-training",
        element: <PflRemovedRedirect to="/support/help-center" />,
      },
      {
        path: "/support/help-center",
        element: <HelpCenterPage />,
      },
      {
        path: "/support/help-center/getting-started/creating-your-account",
        element: <CreatingYourAccount />,
      },
      {
        path: "/support/help-center/getting-started/logging-in-as-student-or-teacher",
        element: <LoggingInAsStudentOrTeacher />,
      },
      {
        path: "/support/help-center/getting-started/setting-up-your-first-class",
        element: <SettingUpYourFirstClass />,
      },
      {
        path: "/support/help-center/getting-started/invite-your-students",
        element: <InviteYourStudents />,
      },
      {
        path: "/support/help-center/getting-started/navigating-the-dashboard",
        element: <NavigatingTheDashboard />,
      },
      {
        path: "/support/help-center/using-the-platform/exploring-curriculum-standards",
        element: <PflRemovedRedirect to="/support/help-center" />,
      },
      {
        path: "/support/help-center/using-the-platform/viewing-student-progress",
        element: <ViewingStudentProgress />,
      },
      {
        path: "/support/help-center/using-the-platform/using-skill-builders",
        element: <PflRemovedRedirect to="/support/help-center" />,
      },
      {
        path: "/support/help-center/using-the-platform/assigning-activities",
        element: <AssigningActivities />,
      },
      {
        path: "/support/help-center/using-the-platform/using-adaptive-instructional-support",
        element: <PflRemovedRedirect to="/support/help-center" />,
      },
      {
        path: "/support/help-center/account-settings/resetting-your-password",
        element: <ResettingYourPassword />,
      },
      {
        path: "/support/help-center/account-settings/managing-notifications",
        element: <ManagingNotifications />,
      },
      {
        path: "/support/help-center/account-settings/connecting-your-lms",
        element: <ConnectingYourLMS />,
      },
      {
        // Run E2 J2 round 3: no profile page exists; the article is unlinked.
        path: "/support/help-center/account-settings/updating-your-profile",
        element: <PflRemovedRedirect to="/support/help-center" />,
      },
      {
        path: "/support/help-center/account-settings/understanding-permissions-and-roles",
        element: <UnderstandingPermissionsAndRoles />,
      },
      {
        path: "/support/help-center/lti-registration-guide",
        element: <LtiRegistrationGuide />,
      },
      {
        path: "/contact-support",
        element: <ContactSupportPage />,
      },
      {
        path: "/student/assessments-grading",
        element: <PflRemovedRedirect to="dashboard" />,
      },
      {
        path: "/curriculum-glance",
        element: <Navigate to="/curriculum" replace />,
      },
      {
        path: "/learning-lab",
        element: <Navigate to="/curriculum" replace />,
      },
      {
        path: "/teacher/grading-assessments",
        element: <PflRemovedRedirect to="dashboard" />,
      },
      {
        path: "/teacher/adaptive-instructional-support",
        element: <PflRemovedRedirect to="dashboard" />,
      },
      {
        path: "/contact",
        element: <Contact />,
      },
      {
        path: "/auth/callback/clever",
        element: <CleverCallbackPage />,
      },
      {
        path: "/state/teacher-guide",
        element: <PflRemovedRedirect to="/curriculum" />,
      },
    ],
  },
  {
    element: (
      <>
        <DashboardLayout />
        <ScrollRestoration />
      </>
    ),
    children: [
      {
        // Teacher preview of a PE lesson — under DashboardLayout so it gets the
        // same shell (global Header: PROJECT EXPLORE brand + user) as the
        // dashboard. Owner-scoped lookup (no student row) + role="teacher" so
        // the doc-hub aside (incl. Assessment tab) renders. Read-only.
        path: "/teacher/lesson-preview/:assignmentId",
        element: (
          <RoleRoute
            allowedRoles={[
              "teacher",
              "admin",
              "district_admin",
              "super_admin",
            ]}
          >
            <TeacherLessonPreview />
          </RoleRoute>
        ),
      },
      {
        path: "/teacher/dashboard",
        element: (
          <RoleRoute
            allowedRoles={[
              "teacher",
              "admin",
              "district_admin",
              "super_admin",
            ]}
          >
            <TeacherDashboard />
          </RoleRoute>
        ),
      },
      {
        path: "/dashboard",
        element: (
          <ProtectedRoute>
            <StudentDashboard />
          </ProtectedRoute>
        ),
      },
      {
        // Run E2 J1: student-teacher chat removed (K-5). /chat and /chat/<id>
        // redirect to the role dashboard (or / when signed out).
        path: "/chat/*",
        element: <ChatRemovedRedirect />,
      },
      {
        path: "/admin/dashboard",
        element: (
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        ),
      },
      {
        path: "/superadmin/dashboard",
        element: (
          <AdminRoute requiredRole="super_admin">
            <SuperAdminDashboard />
          </AdminRoute>
        ),
      },
      {
        path: "/notifications",
        element: (
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        ),
      },
    ],
  },
  {
    element: (
      <>
        <TeacherGuidesLayout />
        <ScrollRestoration />
      </>
    ),
    children: [
      {
        path: "/guides/:standardId/chapters/:chapterId",
        element: <PflRemovedRedirect to="/curriculum" />,
      },
      {
        path: "/additional-resources/:standardId/:chapterId",
        element: <PflRemovedRedirect to="/curriculum" />,
      },
      {
        // Native (non-LTI) PE lesson delivery. Resolves chapter/tier/lang from
        // the student's own assignment row; reuses the shared SceneReader.
        path: "/student/lesson/:assignmentId",
        element: (
          <RoleRoute
            allowedRoles={[
              "student",
              "teacher",
              "admin",
              "district_admin",
              "super_admin",
            ]}
          >
            <StudentLessonPage />
          </RoleRoute>
        ),
      },
      {
        path: "/quiz/:quizId/:assignmentId",
        element: <PflRemovedRedirect to="dashboard" />,
      },
      {
        path: "/quiz/:quizId",
        element: <PflRemovedRedirect to="dashboard" />,
      },
      {
        path: "/midterm/:midtermId/:assignmentId",
        element: <PflRemovedRedirect to="dashboard" />,
      },
      {
        path: "/midterm/:midtermId",
        element: <PflRemovedRedirect to="dashboard" />,
      },
      {
        path: "/midterm-review/:midtermId/:assignmentId",
        element: <PflRemovedRedirect to="dashboard" />,
      },
      {
        path: "/midterm-review/:midtermId",
        element: <PflRemovedRedirect to="dashboard" />,
      },
      {
        path: "/finals/:finalId/:assignmentId",
        element: <PflRemovedRedirect to="dashboard" />,
      },
      {
        path: "/finals-review/:finalId/:assignmentId",
        element: <PflRemovedRedirect to="dashboard" />,
      },
      {
        path: "/finals/:finalId",
        element: <PflRemovedRedirect to="dashboard" />,
      },
      {
        path: "/finals-review/:finalId",
        element: <PflRemovedRedirect to="dashboard" />,
      },
      {
        path: "/project/:projectId/:assignmentId",
        element: <PflRemovedRedirect to="dashboard" />,
      },
      {
        path: "/project/:projectId",
        element: <PflRemovedRedirect to="dashboard" />,
      },

      {
        path: "/project-review/:projectId/:assignmentId",
        element: <PflRemovedRedirect to="dashboard" />,
      },
      {
        path: "/project-review/:projectId",
        element: <PflRemovedRedirect to="dashboard" />,
      },
      {
        path: "/quiz-review/:quizId/:assignmentId",
        element: <PflRemovedRedirect to="dashboard" />,
      },
      {
        path: "/quiz-review/:quizId",
        element: <PflRemovedRedirect to="dashboard" />,
      },
      {
        path: "/submission-review/:submissionId",
        element: (
          <ProtectedRoute>
            <SubmissionReviewPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "/teacher-guides",
        element: <PflRemovedRedirect to="/curriculum" />,
      },
      ...(brandingConfig.brand === "edia"
        ? [
            {
              path: "/",
              element: <EdiaLanding />,
            },
          ]
        : []),
    ],
  },
  {
    element: (
      <>
        <DefaultLayout />
        <ScrollRestoration />
      </>
    ),
    children: [
      {
        path: "/standards/:standardId/chapters/:chapterId/:day",
        element: <PflRemovedRedirect to="dashboard" />,
      },
    ],
  },
  {
    element: (
      <>
        <LtiLayout />
        <ScrollRestoration />
      </>
    ),
    children: [
      {
        path: "/lti/chapter-picker",
        element: (
          <LtiAuthWrapper>
            <LtiChapterPickerPage />
          </LtiAuthWrapper>
        ),
      },
      {
        path: "/lti/resources",
        element: (
          <LtiAuthWrapper>
            <LtiResourcesPage />
          </LtiAuthWrapper>
        ),
      },
      {
        path: "/lti/scenes",
        element: (
          <LtiAuthWrapper>
            <SceneReaderPage />
          </LtiAuthWrapper>
        ),
      },
      {
        path: "/lti/assessment",
        element: (
          <LtiAuthWrapper>
            <FormativeAssessmentPage />
          </LtiAuthWrapper>
        ),
      },
      {
        path: "/lti/quiz",
        element: (
          <LtiAuthWrapper>
            <QuizAssessmentPage />
          </LtiAuthWrapper>
        ),
      },
      {
        path: "/lti/hitl-queue",
        element: (
          <LtiAuthWrapper>
            <HitlQueuePage />
          </LtiAuthWrapper>
        ),
      },
      {
        path: "/lti/submission-review/:submissionId",
        element: (
          <LtiAuthWrapper>
            <SubmissionReviewPage />
          </LtiAuthWrapper>
        ),
      },
      {
        path: "/lti/resources/:resourceId",
        element: <PflRemovedRedirect to="/lti/resources" keepSearch />,
      },
      {
        path: "/auth/callback",
        element: (
          <LtiAuthWrapper>
            <AuthCallback />
          </LtiAuthWrapper>
        ),
      },
    ],
  },
]);
